import { z } from "zod";
import { AuditActivityFilters } from "./AuditActivityFilters";
import { Search, X } from "lucide-react";
import { useState, type SyntheticEvent } from "react";
import {
  AuditOutcomeSchema,
  AuditPageSizeSchema,
  type AuditQuery,
} from "../../shared/audit-schemas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const selectClass =
  "min-h-10 rounded-sm border border-input bg-card px-3 text-sm outline-none focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]";
type FilterProps = { query: AuditQuery; onChange: (query: AuditQuery) => void };

function OutcomeFilters({ query, onChange, clear }: FilterProps & { clear: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Label htmlFor="audit-outcome" className="sr-only">
        Outcome
      </Label>
      <select
        id="audit-outcome"
        className={selectClass}
        value={query.outcome ?? "all"}
        onChange={(event) => {
          onChange({
            ...query,
            outcome:
              event.currentTarget.value === "all"
                ? undefined
                : AuditOutcomeSchema.parse(event.currentTarget.value),
          });
        }}
      >
        <option value="all">All outcomes</option>
        <option value="success">Succeeded</option>
        <option value="denied">Denied</option>
        <option value="error">Failed</option>
      </select>
      <Label htmlFor="audit-page-size" className="sr-only">
        Rows per page
      </Label>
      <select
        id="audit-page-size"
        className={selectClass}
        value={query.pageSize}
        onChange={(event) => {
          onChange({
            ...query,
            pageSize: AuditPageSizeSchema.parse(Number(event.currentTarget.value)),
          });
        }}
      >
        {[10, 25, 50].map((size) => (
          <option key={size} value={size}>
            {size} per page
          </option>
        ))}
      </select>
      <Button type="button" variant="ghost" size="sm" onClick={clear}>
        <X />
        Clear filters
      </Button>
    </div>
  );
}

function SearchFields({
  search,
  period,
  operations,
  setSearch,
  setPeriod,
}: {
  search: string;
  period: string;
  operations: boolean;
  setSearch: (value: string) => void;
  setPeriod: (value: string) => void;
}) {
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <Label htmlFor="audit-search">Search activity</Label>
        <div className="relative mt-2">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-3 left-3 size-4 text-muted-foreground"
          />
          <Input
            id="audit-search"
            className="pl-9"
            type="search"
            maxLength={100}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
            }}
            placeholder={
              operations ? "Action, person, customer, or reference" : "Action, person, or reference"
            }
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="audit-period">Date range</Label>
        <select
          id="audit-period"
          className={selectClass}
          value={period}
          onChange={(event) => {
            setPeriod(event.currentTarget.value);
          }}
        >
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
          <option value="custom">Custom dates</option>
        </select>
      </div>
      <Button type="submit">
        <Search />
        Search
      </Button>
    </div>
  );
}

function CustomDates({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      <div className="grid gap-2">
        <Label htmlFor="audit-from">From</Label>
        <Input
          required
          id="audit-from"
          type="date"
          max={to || undefined}
          value={from}
          onChange={(event) => {
            setFrom(event.currentTarget.value);
          }}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="audit-to">Through</Label>
        <Input
          required
          id="audit-to"
          type="date"
          min={from || undefined}
          value={to}
          onChange={(event) => {
            setTo(event.currentTarget.value);
          }}
        />
      </div>
    </div>
  );
}

function localDate(seconds: number) {
  const date = new Date(seconds * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function AuditFilters({
  query,
  onChange,
  operations,
}: FilterProps & { operations: boolean }) {
  const [reset, setReset] = useState(0);
  const [search, setSearch] = useState(query.q ?? "");
  const [period, setPeriod] = useState(query.from === undefined ? "30" : "custom");
  const [from, setFrom] = useState(query.from === undefined ? "" : localDate(query.from));
  const [to, setTo] = useState(() =>
    query.from === undefined ? "" : localDate(query.to ?? Math.floor(Date.now() / 1000)),
  );
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const now = Math.floor(Date.now() / 1000);
    const dates =
      period === "custom"
        ? {
            from: Math.floor(new Date(`${from}T00:00:00`).getTime() / 1000),
            to: Math.min(now, Math.floor(new Date(`${to}T23:59:59`).getTime() / 1000)),
          }
        : { from: now - Number(period) * 86400, to: undefined };
    const form = new FormData(event.currentTarget);
    const field = (key: string) => {
      return z.string().parse(form.get(key)).trim() || undefined;
    };
    onChange({
      ...query,
      ...dates,
      q: search.trim() || undefined,
      application: field("application"),
      person: field("person"),
      record: field("record"),
    });
  }
  function clear() {
    setReset((value) => value + 1);
    setSearch("");
    setPeriod("30");
    setFrom("");
    setTo("");
    onChange({ pageSize: 10, view: "activity" });
  }
  return (
    <form role="search" aria-label="Audit filters" onSubmit={submit} className="my-6 space-y-3">
      <div role="group" aria-label="Activity view" className="flex gap-1">
        {(["activity", "all"] as const).map((view) => (
          <Button
            key={view}
            type="button"
            variant={query.view === view ? "secondary" : "ghost"}
            aria-pressed={query.view === view}
            onClick={() => {
              onChange({ ...query, view });
            }}
          >
            {view === "activity" ? "Activity" : "All requests"}
          </Button>
        ))}
      </div>
      <SearchFields
        search={search}
        period={period}
        operations={operations}
        setSearch={setSearch}
        setPeriod={setPeriod}
      />
      {period === "custom" ? (
        <CustomDates from={from} to={to} setFrom={setFrom} setTo={setTo} />
      ) : null}
      <AuditActivityFilters key={reset} query={query} onChange={onChange} />
      <OutcomeFilters query={query} onChange={onChange} clear={clear} />
    </form>
  );
}
